import { useCallback, useContext, useEffect, useRef, useState, type SetStateAction } from "react";
import type { z } from "zod";

import { AuthContext } from "../app/authContext";

// Keeps a study form's entries for the life of the browser tab, so a reload, Back or a trip
// through the sidebar does not throw away a half-filled form.
//
// The draft lives in sessionStorage, never localStorage: it is gone when the tab closes, so
// nothing typed on a shared computer waits there for the next person. The key carries the
// user id, so one user never sees another user's entries. A stored draft is used only when it
// still matches the form's schema; anything unreadable is dropped without a word. Every
// storage access is guarded - a private window may refuse it, and the form must keep working
// in memory then.

const DRAFT_KEY_PREFIX = "keos:draft:v1:";

function userPrefix(userId: string): string {
  return `${DRAFT_KEY_PREFIX}${userId}:`;
}

function draftKey(userId: string, module: string): string {
  return `${userPrefix(userId)}${module}`;
}

function removeKey(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to do: there is no other place the draft could be stored.
  }
}

// A draft bound to a project revision (A27 (i)) carries the revision it was started in beside
// its entries, under this key, outside the form's own schema: it is taken off before the
// entries are checked and put back when they are written.
const BOUND_REVISION_KEY = "boundRevisionId";

type Stored<T> = { draft: T; boundRevisionId: string | null };

function readDraft<T>(key: string, schema: z.ZodType<T>, bound: boolean): Stored<T> | null {
  try {
    const stored = window.sessionStorage.getItem(key);
    if (stored === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(stored);
    let entries = parsed;
    let boundRevisionId: string | null = null;
    if (bound && typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      const { [BOUND_REVISION_KEY]: revision, ...rest } = parsed as Record<string, unknown>;
      entries = rest;
      boundRevisionId = typeof revision === "string" && revision !== "" ? revision : null;
    }
    const checked = schema.safeParse(entries);
    if (checked.success) {
      return { draft: checked.data, boundRevisionId };
    }
  } catch {
    // Unreadable: treated like a draft of an older shape below.
  }
  removeKey(key);
  return null;
}

function writeDraft(key: string, draft: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Storage full or refused: the entries still hold for this page, only not across a reload.
  }
}

/** Remove every stored draft of one user (on a deliberate sign-out). */
export function clearStudyDrafts(userId: string): void {
  const prefix = userPrefix(userId);
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key !== null && key.startsWith(prefix)) {
        keys.push(key);
      }
    }
    keys.forEach(removeKey);
  } catch {
    // Storage refused: then nothing was stored either.
  }
}

export type StudyDraftOptions<T> = {
  /**
   * Bind the draft to the active project revision (A27 (i)): the id of the revision open now,
   * or null when none is. Left out, the draft is not bound (every other form).
   */
  revisionId?: string | null;
  /** Short, stable module name, part of the storage key (e.g. "transformer"). */
  module: string;
  /** A fresh empty draft; called on first use, after an unusable restore and on clear. */
  createInitial: () => T;
  /** The shape a stored draft must still have to be restored. */
  schema: z.ZodType<T>;
};

export type StudyDraft<T> = {
  draft: T;
  setDraft: (next: SetStateAction<T>) => void;
  /** True when the entries on screen came back from this tab's storage and are not edited yet. */
  restored: boolean;
  /** Forget the stored draft and start again from an empty form. */
  clear: () => void;
  /**
   * True when a bound draft was started in another revision than the active one; the codes
   * picked from that revision's diagram may not match this one. Stays until the engineer
   * keeps the draft or clears it.
   */
  otherRevision: boolean;
  /** Keep the draft as it is and bind it to the active revision from now on. */
  keepForThisRevision: () => void;
};

type DraftState<T> = {
  key: string | null;
  draft: T;
  restored: boolean;
  /** Only a draft the user has changed is written; an untouched empty form is not stored. */
  edited: boolean;
  /** The revision the draft was started in; null when unbound, unknown (an older draft) or new. */
  boundRevisionId: string | null;
};

function load<T>(key: string | null, options: StudyDraftOptions<T>): DraftState<T> {
  const stored = key === null ? null : readDraft(key, options.schema, options.revisionId !== undefined);
  return stored === null
    ? { key, draft: options.createInitial(), restored: false, edited: false, boundRevisionId: null }
    : {
        key,
        draft: stored.draft,
        restored: true,
        edited: false,
        boundRevisionId: stored.boundRevisionId,
      };
}

export function useStudyDraft<T>(options: StudyDraftOptions<T>): StudyDraft<T> {
  // Read without useAuth on purpose: a form rendered outside the AuthProvider (on its own, in
  // a test) has no user, exactly like a signed-out visitor.
  const auth = useContext(AuthContext);
  const userId = auth?.state.status === "signed-in" ? auth.state.session.user.id : null;
  // Not signed in: plain state in memory, nothing is written.
  const key = userId === null ? null : draftKey(userId, options.module);

  const [state, setState] = useState<DraftState<T>>(() => load(key, options));

  // Another user (or none) now: that user's own draft, never the previous one's.
  if (state.key !== key) {
    setState(load(key, options));
  }

  useEffect(() => {
    if (state.key !== null && state.edited) {
      writeDraft(
        state.key,
        state.boundRevisionId === null
          ? state.draft
          : { ...(state.draft as object), [BOUND_REVISION_KEY]: state.boundRevisionId },
      );
    }
  }, [state]);

  // The latest active revision, for edits made in callbacks that must stay stable.
  const active = options.revisionId ?? null;
  const activeRef = useRef(active);
  activeRef.current = active;

  const setDraft = useCallback((next: SetStateAction<T>) => {
    setState((current) => ({
      ...current,
      draft: typeof next === "function" ? (next as (previous: T) => T)(current.draft) : next,
      // The restore notice only says the form came back; after an edit it is noise.
      restored: false,
      edited: true,
      // A draft is bound to the revision it was started in: the first edit names it, later
      // edits never move it (an older draft of unknown revision is bound at its next edit).
      boundRevisionId: current.boundRevisionId ?? activeRef.current,
    }));
  }, []);

  const { createInitial } = options;
  const clear = useCallback(() => {
    if (key !== null) {
      removeKey(key);
    }
    setState({ key, draft: createInitial(), restored: false, edited: false, boundRevisionId: null });
  }, [key, createInitial]);

  const keepForThisRevision = useCallback(() => {
    setState((current) => ({ ...current, edited: true, boundRevisionId: activeRef.current }));
  }, []);

  const otherRevision =
    options.revisionId !== undefined &&
    active !== null &&
    state.boundRevisionId !== null &&
    state.boundRevisionId !== active;

  return {
    draft: state.draft,
    setDraft,
    restored: state.restored,
    clear,
    otherRevision,
    keepForThisRevision,
  };
}
